import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Instagram, Facebook, Clock } from 'lucide-react';
import Header from './components/Header2';
import Footer from './components/Footer2';
import Button from './components/Button';

import { devLog } from '../../../utils/devLog';
const Contact = () => {
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', subject: '', message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        devLog('Contact form:', formData);
        setSubmitted(true);
    };

    const inputClass = "w-full px-4 py-3 text-sm bg-[#1A1A1A] border border-[#2A2A2A] text-white placeholder-[#525252] focus:outline-none focus:border-[#FF6B35] transition-colors font-['Montserrat']";
    const labelClass = "block text-xs font-semibold text-[#A3A3A3] uppercase tracking-wider mb-2 font-['Montserrat']";

    const contactInfo = [
        { icon: Phone, label: 'Phone', value: '+91 98765 43210' },
        { icon: Mail, label: 'Email', value: 'hello@rang.in' },
        { icon: MapPin, label: 'Address', value: 'Linking Road, Bandra West, Mumbai 400050' },
        { icon: Clock, label: 'Hours', value: 'Mon-Sat: 10AM - 8PM IST' },
    ];

    return (
        <div className="w-full min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
            <Header />

            <main className="pt-[90px] md:pt-[110px] pb-16">
                {/* Hero */}
                <div className="px-4 md:px-6 pb-8 md:pb-12 text-center border-b border-[#2A2A2A]">
                    <p className="text-xs font-medium tracking-[0.3em] uppercase mb-3 text-[#FF6B35] font-['Montserrat']">
                        We'd Love to Hear From You
                    </p>
                    <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider mb-3 text-white font-['Montserrat']">
                        Contact Us
                    </h1>
                </div>

                <div className="max-w-[1000px] mx-auto px-4 md:px-6 pt-10">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                        {/* Contact Info */}
                        <div className="lg:col-span-2">
                            <h2 className="text-lg font-bold text-white mb-6 font-['Montserrat'] uppercase tracking-wider">
                                Get in Touch
                            </h2>
                            <div className="space-y-6">
                                {contactInfo.map(({ icon: Icon, label, value }) => (
                                    <div key={label} className="flex items-start gap-4">
                                        <div className="w-10 h-10 flex items-center justify-center bg-[#1A1A1A] border border-[#2A2A2A] flex-shrink-0">
                                            <Icon className="w-4 h-4 text-[#FF6B35]" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-[#FF6B35] uppercase tracking-wider mb-1 font-['Montserrat']">{label}</p>
                                            <p className="text-sm text-[#A3A3A3] font-['Montserrat']">{value}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Social */}
                            <div className="mt-8">
                                <p className="text-xs font-semibold text-white uppercase tracking-wider mb-3 font-['Montserrat']">Follow Us</p>
                                <div className="flex gap-3">
                                    {[Instagram, Facebook].map((Icon, i) => (
                                        <a
                                            key={i}
                                            href="#"
                                            className="w-10 h-10 flex items-center justify-center border border-[#2A2A2A] hover:border-[#FF6B35] hover:bg-[#FF6B35] transition-all duration-300 group"
                                        >
                                            <Icon className="w-4 h-4 text-[#A3A3A3] group-hover:text-white transition-colors" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-3">
                            {submitted ? (
                                <div className="bg-[#1A1A1A] border border-[#2A2A2A] p-8 text-center">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                                        <Send className="w-6 h-6 text-[#FF6B35]" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2 font-['Montserrat']">Message Sent!</h3>
                                    <p className="text-sm text-[#A3A3A3] mb-6 font-['Montserrat']">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                                    <Button variant="secondary" onClick={() => setSubmitted(false)}>Send Another</Button>
                                </div>
                            ) : (
                                <div className="bg-[#1A1A1A] border border-[#2A2A2A] p-6 md:p-8">
                                    <h2 className="text-lg font-bold text-white mb-6 font-['Montserrat'] uppercase tracking-wider">
                                        Send a Message
                                    </h2>
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelClass}>Name</label>
                                                <input name="name" value={formData.name} onChange={handleInputChange} placeholder="Your name" className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Email</label>
                                                <input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="you@email.com" className={inputClass} required />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelClass}>Phone</label>
                                                <input name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+91 98765 43210" className={inputClass} />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Subject</label>
                                                <input name="subject" value={formData.subject} onChange={handleInputChange} placeholder="How can we help?" className={inputClass} required />
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Message</label>
                                            <textarea name="message" value={formData.message} onChange={handleInputChange} placeholder="Tell us more..." rows={5} className={`${inputClass} resize-none`} required />
                                        </div>
                                        <Button variant="primary" size="lg" fullWidth type="submit" className="gap-2">
                                            <Send className="w-4 h-4" /> Send Message
                                        </Button>
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Contact;
